'use client';

import { useState } from 'react';
import { useToastStore } from '@/stores/toast.store';
import { useConsultation } from '@/entities/hooks/useConsultation';
import { regexEmail } from '@/libs/constants/regex';
import { formatPhoneNumber } from '@/shared/lib/form';
import { Typography } from '@/shared/ui/atoms';
import { InterestServices } from '@/types/api';
import { ConsultLabelInputRow, ConsultServiceCheckCard, ConsultSubmit } from '@/widgets/landing/modals/ui/molecules';

interface ConsultModalBodyProps {
  onClose: () => void;
}

const ConsultModalBody = ({ onClose }: ConsultModalBodyProps) => {
  const { setShowToast, setToastInfos } = useToastStore((state) => state);
  const [name, setName] = useState('');
  const [hospitalName, setHospitalName] = useState('');
  const [region, setRegion] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isCheckedReviewManager, setIsCheckedReviewManager] = useState(false);
  const [isCheckedReviewBooster, setIsCheckedReviewBooster] = useState(false);
  const [isCheckedMedicalContent, setIsCheckedMedicalContent] = useState(false);
  const [isCheckedAISearchOptimization, setIsCheckedAISearchOptimization] = useState(false);
  const [content, setContent] = useState('');
  const [isAgreed, setIsAgreed] = useState(false);
  const { mutate } = useConsultation({
    onSuccess: () => {
      setToastInfos({ text: '상담 신청이 완료되었습니다.' });
      setShowToast(true);
      onClose();
    },
    onError: () => {
      setToastInfos({ text: '상담 신청이 실패했습니다.\n관리자에게 문의해주세요.' });
      setShowToast(true);
    }
  });

  const onChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
  };

  const checkValidation = () => {
    if (!name) {
      return '성함(직책)을 입력해주세요.';
    }
    if (!hospitalName) {
      return '병원명을 입력해주세요.';
    }
    if (!region) {
      return '지역을 입력해주세요.';
    }
    if (!phone) {
      return '연락처를 입력해주세요.';
    }
    if (!email) {
      return '이메일을 입력해주세요.';
    }
    if (email && !regexEmail.test(email)) {
      return '이메일 형식이 맞지 않습니다.';
    }
    if (!isCheckedReviewManager && !isCheckedReviewBooster && !isCheckedMedicalContent && !isCheckedAISearchOptimization) {
      return '관심서비스를 선택해주세요.';
    }
    if (!content) {
      return '내용을 입력해주세요.';
    }
    if (content.length < 10) {
      return '내용을 10자 이상 입력해주세요.';
    }
    if (!isAgreed) {
      return '개인정보 수집 및 이용 동의를 동의해주세요.';
    }
    return '';
  };

  const callApi = async () => {
    const interestServices: InterestServices[] = [];
    if (isCheckedReviewManager) {
      interestServices.push(InterestServices.REVIEW_MANAGER);
    }
    if (isCheckedReviewBooster) {
      interestServices.push(InterestServices.REVIEW_BOOST);
    }
    if (isCheckedMedicalContent) {
      interestServices.push(InterestServices.MEDICONTENTS);
    }
    if (isCheckedAISearchOptimization) {
      interestServices.push(InterestServices.AI_ENGINE_OPTIMIZER);
    }

    mutate({
      applicantName: name,
      hospitalName: hospitalName,
      region: region,
      contactNumber: phone,
      email: email,
      interestServices: interestServices,
      description: content,
      agreedToTerms: isAgreed
    });
  };

  const handleSubmit = () => {
    const errorMessage = checkValidation();
    if (errorMessage) {
      setToastInfos({ text: errorMessage });
      setShowToast(true);
      return;
    }
    callApi();
  };

  return (
    <div className="relative flex flex-col gap-[1.88rem] md:gap-[1.25rem] w-full md:w-[658px] p-[1rem] md:pt-[0.75rem] md:px-[2rem] md:pb-[1.88rem]">
      <div className="flex flex-col gap-[0.88rem] md:gap-[0.5rem]">
        <ConsultLabelInputRow label="성함(직책)" placeholder="김진아(원장)" maxLength={10} value={name} essential={true} onChange={(e) => setName(e.target.value)} />
        <ConsultLabelInputRow label="병원명" placeholder="소속 병원명을 입력해주세요." maxLength={20} value={hospitalName} essential={true} onChange={(e) => setHospitalName(e.target.value)} />
        <ConsultLabelInputRow label="지역" placeholder="경기도 성남시 분당구" maxLength={20} value={region} essential={true} type="text" onChange={(e) => setRegion(e.target.value)} />
        <ConsultLabelInputRow label="연락처" placeholder="‘-’제외하고 숫자만 입력해주세요." maxLength={12} value={phone} essential={true} type="tel" onChange={onChangePhone} />
        <ConsultLabelInputRow label="이메일" placeholder="cs@legalcare.ai" maxLength={30} value={email} essential={true} type="email" onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="flex flex-col md:items-start justify-between gap-[0.47rem] md:gap-[1rem]">
        <div className="flex gap-[0.25rem] md:gap-[0.25rem]">
          <Typography as="p" className="text-[0.938rem] md:text-[1.125rem] leading-[1.375rem] font-medium">
            관심서비스 <span className="relative -top-[1px] text-[1rem] leading-[1.125rem] font-medium text-[#EA4B23]">*</span>
          </Typography>
          <Typography as="p" className="text-[0.938rem] md:text-[1rem] leading-[1.375rem] font-medium text-gray-400">
            (복수선택 가능)
          </Typography>
        </div>
        <div className="grid md:grid-cols-2 md:grid-rows-2 md:grid-flow-col gap-[0.5rem] w-full">
          <ConsultServiceCheckCard
            checked={isCheckedReviewManager}
            onChange={() => setIsCheckedReviewManager(!isCheckedReviewManager)}
            name="리뷰매니저"
            description="리뷰·커뮤니티 통합 모니터링 및 관리"
          />
          <ConsultServiceCheckCard checked={isCheckedReviewBooster} onChange={() => setIsCheckedReviewBooster(!isCheckedReviewBooster)} name="리뷰부스터" description="실제 환자 리뷰 전환 자동화" />
          <ConsultServiceCheckCard
            checked={isCheckedMedicalContent}
            onChange={() => setIsCheckedMedicalContent(!isCheckedMedicalContent)}
            name="메디컨텐츠"
            description="SEO 최적화 블로그 콘텐츠 제작 대행"
          />
          <ConsultServiceCheckCard
            checked={isCheckedAISearchOptimization}
            onChange={() => setIsCheckedAISearchOptimization(!isCheckedAISearchOptimization)}
            name="AI 검색 최적화 서비스"
            description="AI 답변 엔진 최적화(GEO, AEO 최적화) 및 모니터링"
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row items-start justify-between gap-[0.5rem]">
        <Typography as="p" className="md:flex-shrink-0 text-[1.125rem] leading-[1.375rem] font-medium">
          내용 <span className="text-[1rem] leading-[1.125rem] font-medium text-red-500">*</span>
        </Typography>
        <textarea
          maxLength={1000}
          placeholder="상담하고 싶은 내용을 입력해주세요. (10자 이상)"
          className="w-full md:w-[21.4375rem] h-[8.125rem] p-[1rem] border border-gray-200 rounded-lg text-[1rem] leading-[1.5rem] font-medium placeholder:text-gray-400"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </div>
      <ConsultSubmit isAgreed={isAgreed} onAgreedChange={() => setIsAgreed(!isAgreed)} onSubmit={handleSubmit} />
    </div>
  );
};

export default ConsultModalBody;
